/**
 * Claim-work page, queue-aware (GDN-3381).
 *
 * Hand-written over the same `/v2/provider/billing/v2/claim-work` route the
 * generated client already serves, for one reason: the generated arg type is
 * pinned to the pre-queue OpenAPI document, so it can neither carry
 * `include_hidden` nor describe the rows the queue view returns. Packet F
 * regenerates the client and this file collapses into it.
 *
 * The types below are hand-pinned from `tasks-queues-contract-v1.md` §§1, 4,
 * 5 and nowhere else. Every addition is OPTIONAL: a server that has not yet
 * shipped the view returns today's rows, and this list still renders.
 *
 * It provides the same `BillingV2ClaimWork` tag the generated endpoint does,
 * so the work-disposition verbs (packet D) invalidate both with one tag.
 */
import { baseApi } from '@store/baseApi';

import type {
  ClaimWorkCountsAdditions,
  ClaimWorkRowAdditions,
  WorkViewAliasKey,
  WorkViewKey,
} from '../types/encounterWork';

import type { ClaimWorkCounts, ClaimWorkItem, ClaimWorkSortKey } from './v2-api';
import { V2_TAGS } from './v2-api';

/**
 * A `get_claim_work_page` row: today's item plus contract §5.
 *
 * The additions are PARTIAL on purpose. The field names come from the shared
 * kit, so there is one spelling of the contract; the optionality is this
 * list's own, because a server that has not yet shipped the queue view
 * returns today's rows and this list still has to render them.
 */
export type ClaimWorkQueueItem = ClaimWorkItem & Partial<ClaimWorkRowAdditions>;

/** Counts for all seven keys, plus the hidden tally (contract §4). */
export type ClaimWorkQueueCounts = ClaimWorkCounts & Partial<ClaimWorkCountsAdditions>;

export interface ClaimWorkPageResponse {
  counts: ClaimWorkQueueCounts;
  items?: ClaimWorkQueueItem[];
  next_cursor?: string | null;
}

export interface ClaimWorkPageArgs {
  /**
   * The seven keys plus the two aliases, because the caller sends the key AS
   * STORED. `needs_correction` is the union of review and fix on the server,
   * not a synonym for `fix`, so resolving it before the request would drop
   * half the rows a saved view was built to hold.
   */
  workView?: WorkViewKey | WorkViewAliasKey;
  providerIds?: string;
  payerIds?: string;
  patientIds?: string;
  claimLifecycles?: string;
  serviceDateWindowDays?: number;
  serviceDateFrom?: string;
  serviceDateTo?: string;
  sort?: ClaimWorkSortKey;
  limit?: number;
  cursor?: string;
  /** Queue views only. Lists ignore it, so the client never sends it there. */
  includeHidden?: boolean;
}

const compactParams = (params: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ''),
  );

const claimWorkApi = baseApi
  .enhanceEndpoints({ addTagTypes: [V2_TAGS.CLAIM_WORK] })
  .injectEndpoints({
    endpoints: build => ({
      getClaimWorkPage: build.query<ClaimWorkPageResponse, ClaimWorkPageArgs>({
        query: args => ({
          url: '/v2/provider/billing/v2/claim-work',
          params: compactParams({
            work_view: args.workView,
            provider_ids: args.providerIds,
            payer_ids: args.payerIds,
            patient_ids: args.patientIds,
            claim_lifecycles: args.claimLifecycles,
            service_date_window_days: args.serviceDateWindowDays,
            service_date_from: args.serviceDateFrom,
            service_date_to: args.serviceDateTo,
            sort: args.sort,
            limit: args.limit,
            cursor: args.cursor,
            /* Absent, not `false`, when off: an untouched list must read
               exactly as it did before the toggle existed. */
            include_hidden: args.includeHidden ? true : undefined,
          }),
        }),
        providesTags: [V2_TAGS.CLAIM_WORK],
      }),
    }),
  });

export const { useGetClaimWorkPageQuery: useClaimWorkPageQuery } = claimWorkApi;

export { claimWorkApi };
